import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Lock, LogOut, Plus, Trash2, Loader2, CalendarClock, CalendarX2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "crk-admin-token";

const inputCls = "w-full bg-teal-deep/50 border border-gold/25 rounded-sm px-4 py-3 text-cream placeholder:text-cream/40 focus:border-gold outline-none transition-colors";

const fmtDE = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

export const AdminPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ date: "", kind: "closed", open: "20:00", close: "02:00", note: "" });

  const authHeader = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/special-days`, authHeader());
      setDays(data.special_days || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Sonderzeiten konnten nicht geladen werden.");
      }
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anmeldung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/special-days`, form, authHeader());
      toast.success("Sonderzeit gespeichert.");
      setForm({ date: "", kind: "closed", open: "20:00", close: "02:00", note: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/special-days/${id}`, authHeader());
      toast.success("Eintrag gelöscht.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  return (
    <div className="min-h-screen bg-teal-dark grain">
      <Toaster position="top-center" theme="dark" richColors />
      <div className="max-w-xl mx-auto px-5 py-16">
        <img src="/logo.png" alt="Ceylon Royal Kitchen" className="h-20 mx-auto rounded-sm" />
        <h1 className="font-serif text-4xl text-cream text-center mt-6">Sonderzeiten & Ruhetage</h1>
        <p className="text-cream/50 text-sm text-center mt-2">
          Lege Tage fest, an denen ihr geschlossen habt oder zu anderen Zeiten öffnet.
          Bestellungen und das Geöffnet-Badge passen sich automatisch an.
        </p>

        {!token ? (
          <form onSubmit={login} data-testid="admin-login-form" className="mt-10 border border-gold/25 rounded-sm bg-teal-deep p-6 space-y-4">
            <div className="flex items-center gap-2 text-gold overline"><Lock size={14} /> Admin-Login</div>
            <input
              type="password"
              required
              data-testid="admin-password-input"
              className={inputCls}
              placeholder="Admin-Passwort"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="submit" disabled={loading} data-testid="admin-login-btn"
              className="w-full flex items-center justify-center gap-2 bg-gold text-teal-dark font-medium py-3.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={17} />} Anmelden
            </button>
          </form>
        ) : (
          <div className="mt-10 space-y-6" data-testid="admin-panel">
            <form onSubmit={save} data-testid="special-day-form" className="border border-gold/25 rounded-sm bg-teal-deep p-6 space-y-4">
              <div className="flex items-center gap-2 text-gold overline"><Plus size={14} /> Neuen Eintrag anlegen</div>
              <div className="grid grid-cols-2 gap-3">
                <input type="date" required data-testid="special-date-input" className={inputCls}
                  value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                <select data-testid="special-kind-select" className={inputCls}
                  value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="closed">Ruhetag (geschlossen)</option>
                  <option value="custom">Sonderzeiten</option>
                </select>
              </div>
              {form.kind === "custom" && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-cream/60 text-xs">
                    Geöffnet ab
                    <input type="time" required data-testid="special-open-input" className={`${inputCls} mt-1`}
                      value={form.open} onChange={(e) => setForm({ ...form, open: e.target.value })} />
                  </label>
                  <label className="text-cream/60 text-xs">
                    Geöffnet bis
                    <input type="time" required data-testid="special-close-input" className={`${inputCls} mt-1`}
                      value={form.close} onChange={(e) => setForm({ ...form, close: e.target.value })} />
                  </label>
                </div>
              )}
              <input data-testid="special-note-input" className={inputCls}
                placeholder="Notiz für Gäste (optional, z. B. Familienfeier)"
                value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              <button type="submit" disabled={loading} data-testid="special-save-btn"
                className="w-full flex items-center justify-center gap-2 bg-gold text-teal-dark font-medium py-3.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={17} />} Speichern
              </button>
            </form>

            <div className="border border-gold/25 rounded-sm bg-teal-deep p-6">
              <div className="flex items-center gap-2 text-gold overline mb-4"><CalendarClock size={14} /> Eingetragene Tage</div>
              {days.length === 0 ? (
                <p className="text-cream/50 text-sm" data-testid="special-empty">Keine Sonderzeiten eingetragen – es gelten die normalen Öffnungszeiten.</p>
              ) : (
                <ul className="space-y-3" data-testid="special-day-list">
                  {days.map((d) => (
                    <li key={d.id} data-testid={`special-day-${d.date}`}
                      className="flex items-center justify-between gap-3 border border-gold/15 rounded-sm px-4 py-3">
                      <div className="flex items-center gap-3">
                        {d.kind === "closed"
                          ? <CalendarX2 size={18} className="text-red-300 shrink-0" />
                          : <CalendarClock size={18} className="text-gold shrink-0" />}
                        <div>
                          <p className="text-cream text-sm font-medium">{fmtDE(d.date)}</p>
                          <p className="text-cream/55 text-xs">
                            {d.kind === "closed" ? "Geschlossen" : `Sonderzeiten: ${d.open} – ${d.close} Uhr`}
                            {d.note && ` · ${d.note}`}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => remove(d.id)} data-testid={`special-delete-${d.date}`}
                        aria-label={`Eintrag ${fmtDE(d.date)} löschen`}
                        className="text-cream/40 hover:text-red-300 transition-colors">
                        <Trash2 size={17} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button onClick={logout} data-testid="admin-logout-btn"
              className="w-full flex items-center justify-center gap-2 text-cream/50 hover:text-cream text-sm py-2 transition-colors">
              <LogOut size={15} /> Abmelden
            </button>
            <a href="/" className="block text-center text-gold/70 hover:text-gold text-xs transition-colors">← Zurück zur Website</a>
          </div>
        )}
      </div>
    </div>
  );
};
